import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MenuController, ModalController } from '@ionic/angular';
import { firstValueFrom, from, map, takeUntil } from 'rxjs';
import { Unsubscriber } from 'src/app/core/unsubscriber/unsubscriber';
import { Gastro } from 'src/app/maps/domain/masterdata/gastro.model';
import { Lift } from 'src/app/maps/domain/masterdata/lift.model';
import { MapElement } from 'src/app/maps/domain/masterdata/map-element.model';
import { POI } from 'src/app/maps/domain/masterdata/poi.model';
import { Slope } from 'src/app/maps/domain/masterdata/slope.model';
import { Trail } from 'src/app/maps/domain/masterdata/trail.model';
import { AssetSidepaneContentComponent } from 'src/app/maps/map/asset-sidepane/asset-sidepane-content/asset-sidepane-content.component';
import { HoverService } from 'src/app/maps/map/hover.service';
import { MapStateService } from 'src/app/maps/map/map-state.service';
import { MapStyleService } from 'src/app/maps/map/style/map-style.service';

@Component({
  selector: 'sis-asset-sidepane',
  templateUrl: './asset-sidepane.component.html',
  styleUrls: ['./asset-sidepane.component.scss'],
  standalone: false,
})
export class AssetSidepaneComponent extends Unsubscriber implements OnInit {
  @Input() editMode: boolean;
  @Input() bigScreenMode: boolean;

  showSpinner = true;
  showQrCode = false;
  element: MapElement;

  readonly spinnerColor$ = from(this.mapStyleService.getStyleName()).pipe(
    map((name) => (name === 'andermatt' ? 'dark' : 'secondary')),
  );

  private bottomSheetModal?: HTMLIonModalElement;
  private bottomSheetElementGuid?: string;
  private bottomSheetRequestId = 0;

  constructor(
    private mapStateService: MapStateService,
    private mapStyleService: MapStyleService,
    private hoverService: HoverService,
    private modalCtrl: ModalController,
    private menuCtrl: MenuController,
    private activatedRoute: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit(): void {
    this.activatedRoute.queryParamMap.pipe(takeUntil(this.onDestroy$)).subscribe((paramMap) => {
      this.showQrCode = paramMap.get('qrcode')?.toLowerCase() === 'true';
    });

    this.mapStateService.selectedElement$.pipe(takeUntil(this.onDestroy$)).subscribe(async ({ element }) => {
      if (element == null || !this.isBottomSheetElement(element)) {
        this.bottomSheetRequestId++;

        if (!this.bottomSheetModal) {
          return;
        }

        const modal = this.bottomSheetModal;
        this.bottomSheetModal = undefined;
        this.bottomSheetElementGuid = undefined;
        await modal.dismiss();
      }
    });

    this.mapStateService.elementClicked$.pipe(takeUntil(this.onDestroy$)).subscribe(async ({ element }) => {
      if (element == null) {
        return;
      }
      this.element = element;
      if (!this.editMode && this.isBottomSheetElement(element)) {
        this.showSpinner = false;
        if (this.bigScreenMode) {
          await this.menuCtrl.open('asset-sidepane-menu');
        } else {
          await this.createBottomSheetModal(element);
        }
      }
    });
  }

  private async createBottomSheetModal(element: MapElement): Promise<void> {
    if (this.bottomSheetElementGuid === element.guid) {
      return;
    }

    const requestId = ++this.bottomSheetRequestId;
    const initialBreakpoint = element instanceof Gastro ? 0.8 : 0.5;

    if (this.bottomSheetModal) {
      this.bottomSheetElementGuid = element.guid;
      await this.bottomSheetModal.setCurrentBreakpoint(initialBreakpoint);
      return;
    }

    const modal = await this.modalCtrl.create({
      component: AssetSidepaneContentComponent,
      breakpoints: [0, 0.3, 0.5, 0.8, 0.9999],
      initialBreakpoint,
      handle: true,
      cssClass: 'sis-sismap-bottom-sheet-div',
      mode: 'ios',
      showBackdrop: false,
    });

    if (requestId !== this.bottomSheetRequestId) {
      modal.remove();
      return;
    }

    this.bottomSheetModal = modal;
    this.bottomSheetElementGuid = element.guid;
    await modal.present();
    modal.onWillDismiss().then(async () => {
      const dismissedElementGuid = this.bottomSheetElementGuid;
      if (this.bottomSheetModal === modal) {
        this.bottomSheetModal = undefined;
        this.bottomSheetElementGuid = undefined;
      }

      const selectedElement = await firstValueFrom(this.mapStateService.selectedElement$);
      if (dismissedElementGuid && selectedElement.element?.guid === dismissedElementGuid) {
        this.mapStateService.clickElement(null, null);
      }

      if (dismissedElementGuid) {
        this.hoverService.hover({ guid: dismissedElementGuid, hover: false, iconHovered: false });
      }
    });
  }

  private isBottomSheetElement(element: MapElement): boolean {
    return (
      element instanceof Lift ||
      element instanceof Slope ||
      element instanceof Trail ||
      element instanceof POI ||
      element instanceof Gastro
    );
  }

  resetMenu(): void {
    this.showSpinner = true;
    this.mapStateService.clickElement(null, null);
    this.hoverService.hover({ guid: this.element.guid, hover: false, iconHovered: false });
  }
}
